import Vue from 'vue';
import _ from '../../../utils';
import IValidationError from '../../validation/IValidationError';
import { IJSONSchema } from '../../validation/IJSONSchema';

const primitivePropNames = Object.getOwnPropertyNames(Object.prototype);

export default abstract class BaseModel<Tdto> {
  protected dto: Tdto;
  protected originalDto: Tdto;
  // eslint-disable-next-line no-use-before-define
  protected innerObjModels: Record<string, BaseModel<any>> = {};
  protected innerArrayModelsOriginals: Record<string, any[]> = {};
  // eslint-disable-next-line no-use-before-define
  protected innerArrayModels: Record<string, BaseModel<any>[]> = {};

  public initalized: boolean = false;

  constructor() {
    this.dto = {} as any;
    this.originalDto = {} as any;

    // TODO: Properly fix tests that fail, make sure it doesn't break productive use-cases
    // The inner workings of a Model should not be copied over or evaluated
    // when looping through the properties
    // Object.defineProperty(this, 'dto', {
    //   enumerable: false,
    //   configurable: false,
    // });
    // Object.defineProperty(this, 'originalDto', {
    //   enumerable: false,
    //   configurable: false,
    // });
    // Object.defineProperty(this, 'innerObjModels', {
    //   enumerable: false,
    //   configurable: false,
    // });
    // Object.defineProperty(this, 'innerArrayModelsOriginals', {
    //   enumerable: false,
    //   configurable: false,
    // });
    // Object.defineProperty(this, 'innerArrayModels', {
    //   enumerable: false,
    //   configurable: false,
    // });

    // Copy setters and getter from prototype to actual instance, this has several advantages
    // This way the properties will be enumerable and they will survive a spread
    // This should work with transpiled and native ES6 getter/setter variants (tested)
    // As this is a class it will always have a prototype
    const prototypeChain = [];
    const queue = [Object.getPrototypeOf(this)];
    // Traverse the prototype chain (needed for inheritance)
    while (queue.length > 0) {
      const currObj = queue.pop();
      prototypeChain.push(currObj);

      const nextObj = Object.getPrototypeOf(currObj);

      // Not the nicest solution but it works
      // It prevents the BaseModel properties being copied directly onto the model instance
      // TODO: Make this compile-time checked
      const isBaseModel = nextObj?.constructor?.name === 'BaseModel';

      // Should not cause an endless loop for self-referential chains
      if (!isBaseModel && nextObj && !prototypeChain.includes(nextObj)) {
        queue.push(nextObj);
      }
    }

    // Must reverse order for proper shadowing of members
    prototypeChain.reverse();

    prototypeChain.forEach((currObj) => {
      const propnames = Object.getOwnPropertyNames(currObj);
      propnames.forEach((propName) => {
        if (primitivePropNames.includes(propName)) {
          // Skip any propNames that are part of JS
          return;
        }
        const descriptor = Object.getOwnPropertyDescriptor(currObj, propName);
        const hasGetterOrSetter = descriptor?.get || descriptor?.set;
        if (hasGetterOrSetter) {
          Object.defineProperty(this, propName, {
            get: descriptor?.get?.bind(this),
            set: descriptor?.set?.bind(this),
            enumerable: true,
            configurable: true,
          });
        }
      });
    });
  }

  /** Populate data from a DTO */
  public fromDTO(dto: Tdto) {
    this.dto = _.deepClone(dto);
    this.transformFromDTO();
    this.originalDto = Object.freeze(_.deepClone(dto));
    // TODO: Refactor into some kind of separate initialisation --> less dependencies
    Object.keys(this).forEach((key) => {
      const desc = Object.getOwnPropertyDescriptor(this, key);
      if (desc?.get) {
        // HACK: Touch getter in order to initialize all parts of the model
        // This way the getObjectModel and getArrayModels are called for sure
        const foo = (this as any)[key];
      }
    });
    this.initalized = true;
    return this;
  }

  public fromQuery(queries: Record<string, string>) {
    this.transformFromQuery(queries);
    this.fromDTO(this.dto);
  }

  /** Transforms the model for use in URL Queries */
  public toQuery(): Record<string, string> {
    return this.transformToQuery();
  }

  /** Load data from an object that matches the ViewModel properties */
  public fromModel(obj: Partial<Omit<this, keyof BaseModel<Tdto>>>) {
    class EmptyModel extends BaseModel<any> {
      protected transformToDTO(): void {}
      protected transformFromDTO(): void {}
    }
    const emptyModel = new EmptyModel();

    const forbiddenKeys = [
      ...Object.keys(emptyModel),
      _.nameof(() => this.isDirty),
      _.nameof(() => this.valid),
    ];

    Object.keys(obj).forEach((key) => {
      if (forbiddenKeys.includes(key)) {
        return;
      }
      const value = (obj as any)[key];
      const descriptor = Object.getOwnPropertyDescriptor(this, key);
      const isNotWritable = !(descriptor?.writable ?? true);
      const onlyGetter = descriptor?.get && !descriptor?.set;
      const isReadonly = isNotWritable || onlyGetter;
      if (!descriptor || !isReadonly) {
        (this as any)[key] = value;
      }
    });
    this.transformInnerPropertiesToDTO();
    this.transformFromModel();
    this.transformToDTO();
    // This severs all references too so no accidental mutations of the original object
    this.fromDTO(this.dto);
    this.initalized = true;
    return this;
  }

  /** Has the model changed since data was originally loaded using fromDTO, fromModel or fromQuery */
  get isDirty() {
    if (this.initalized === false) {
      return false;
    }
    const innerObjKeys = Object.keys(this.innerObjModels);
    const innerArrayKeys = Object.keys(this.innerArrayModels);
    let isDirty = false;
    const objectQueue = [] as {value: any, originalValue: any}[];
    const filteredProps = Object.keys(this.dto as any).filter((key) => !innerObjKeys.includes(key) && !innerArrayKeys.includes(key));
    const filteredDto: Record<string, any> = {};
    // Filter non-model props
    filteredProps.forEach((key) => {
      filteredDto[key] = (this.dto as any)[key];
    });
    objectQueue.push({ value: filteredDto, originalValue: this.originalDto });
    // Check primitives (non-models)
    // Needs recursion in case a model does not exist yet
    while (objectQueue.length > 0 && isDirty === false) {
      const obj = objectQueue.pop();
      // TODO: Cleanup linting errors
      Object.keys(obj?.value).forEach((key) => {
        const value = obj?.value[key];
        const originalValue = obj?.originalValue[key];
        if (_.isObject(value)) {
          const isRecursive = objectQueue.find((obj) => obj.value === value) || obj?.value === value;
          if (!isRecursive && value !== originalValue) {
            objectQueue.push({ value, originalValue });
          }
        } else if (Array.isArray(value)) {
          if (!Array.isArray(originalValue)) {
            isDirty = true;
            return; // Early-out
          }
          if ((value as any[]).length !== originalValue.length) {
            isDirty = true;
            return; // Early-out
          }
          (value as any[]).forEach((arrayValue, index) => {
            if (_.isObject(arrayValue) && _.isObject(originalValue[index])) {
              const isRecursive = objectQueue.find((obj) => obj.value === arrayValue) || obj?.value === arrayValue;
              if (!isRecursive && arrayValue !== originalValue[index]) {
                objectQueue.push({ value: arrayValue, originalValue: originalValue[index] });
              }
            } else if (arrayValue !== originalValue[index]) {
              isDirty = true;
              // Early-out
            }
          });
        } else if (value !== originalValue) {
          isDirty = true;
          // Early-out
        }
      });
    }
    if (isDirty) {
      return isDirty; // Early-out
    }
    // Check models
    Object.keys(this.innerObjModels).forEach((key) => {
      const objModel = this.innerObjModels[key];
      if (objModel.isDirty) {
        isDirty = true;
        // Early-out
      }
    });
    if (isDirty) {
      return isDirty; // Early-out
    }
    // Check array of models
    Object.keys(this.innerArrayModels).forEach((key) => {
      const modelArray = this.innerArrayModels[key];
      if (this.getArrayModelsDirty(key)) {
        isDirty = true;
        return; // Early-out
      }
      modelArray.forEach((model) => {
        if (model.isDirty) {
          isDirty = true;
          // Early-out
        }
      });
      if (isDirty) {
        // Early-out
      }
    });
    return isDirty;
  }

  protected setObjectModel<T extends BaseModel<any>>(exp: ()=>any, model: T) {
    const dtoPath = _.nameof(exp)?.replace('dto.', '');
    if (!dtoPath) {
      Vue.$log.error(`Unable to set Object Model for ${exp.toString()}`);
      return;
    }
    this.innerObjModels[dtoPath] = model;
  }

  protected getObjectModel<TModel extends BaseModel<any>>(exp: ()=>any, ModelType: new() => TModel): TModel {
    const dtoPath = _.nameof(exp)?.replace('dto.', '');
    if (!dtoPath) {
      throw new Error(`Unable to get Object Model for ${exp.toString()}`);
    }
    if (!this.innerObjModels[dtoPath]) {
      const dto = _.deepGet(this.dto, dtoPath);
      if (dto === undefined || dto === null) {
        return dto;
      }
      this.innerObjModels[dtoPath] = new ModelType().fromDTO(dto);
    }
    return this.innerObjModels[dtoPath] as TModel;
  }

  protected updateArrayModelOriginals(key: string, originalArray: any[]) {
    // Shallow copy
    this.innerArrayModelsOriginals[key] = [...originalArray];
  }

  protected getArrayModelsDirty(key: string) {
    const array = this.innerArrayModels[key];
    const originalArray = this.innerArrayModelsOriginals[key];
    if (array.length !== originalArray.length) {
      return true;
    }
    for (let i = 0; i < array.length; i += 1) {
      if (array[i] !== originalArray[i]) {
        return true;
      }
    }
    return false;
  }

  protected setArrayModels<T extends BaseModel<any>>(exp: ()=>any, models: T[]) {
    if (!_.isArray(models)) {
      Vue.$log.error(`Unable to set Array Models for ${exp.toString()}, models is not an array`);
      return;
    }

    const dtoPath = _.nameof(exp)?.replace('dto.', '');
    if (!dtoPath) {
      Vue.$log.error(`Unable to set Array Models for ${exp.toString()}`);
      return;
    }
    const savedArray = this.innerArrayModels[dtoPath];
    if (Array.isArray(savedArray)) {
      // Keep reference
      savedArray.splice(0, savedArray.length);
      models.forEach((model) => {
        savedArray.push(model);
      });
    } else {
      this.updateArrayModelOriginals(dtoPath, models);
      this.innerArrayModels[dtoPath] = models;
    }
  }

  protected getArrayModels<TModel extends BaseModel<any>>(exp: ()=>any, ModelType: new() => TModel) {
    const dtoPath = _.nameof(exp)?.replace('dto.', '');
    if (!dtoPath) {
      Vue.$log.error(`Unable to set Array Models for ${exp.toString()}, unable to resolve dto path`);
      return [] as TModel[];
    }
    if (!this.innerArrayModels[dtoPath]) {
      const dtoArray = _.deepGet(this.dto, dtoPath) ?? [];
      if (!Array.isArray(dtoArray)) {
        Vue.$log.error(`[BaseModel] Trying to cast ${dtoPath} to ${ModelType.name}[], but it's not an array --> ${typeof dtoArray}. Aborting cast!`);
        return [] as TModel[];
      }
      const modelArray = [] as any[];
      dtoArray.forEach((dto) => {
        modelArray.push(new ModelType().fromDTO(dto));
      });
      this.updateArrayModelOriginals(dtoPath, modelArray);
      this.innerArrayModels[dtoPath] = modelArray;
    }
    return this.innerArrayModels[dtoPath] as any as TModel[];
  }

  // These are hooks for child classes__________

  /**
   * @virtual
   * Called when converting from a query
   */
  protected transformFromQuery(queries: Record<string, string>): void {
    Object.keys(queries).forEach((key) => {
      Vue.set(this.dto as any, key, queries[key]);
    });
  }

  /**
   * @virtual
   * Called when converting to url queries
   */
  protected transformToQuery(): Record<string, string> {
    const queries = {} as Record<string, string>;
    Object.keys(this.dto as any).forEach((key) => {
      const value = (this.dto as any)[key];
      if (!_.isObject(value) && !_.isArray(value) && value !== undefined && value !== '') {
        queries[key.toString().toLowerCase()] = (value as any)?.toString();
      }
    });
    return queries;
  }

  /**
   * @virtual
   * Called before transforming data from a different model
   */
  protected transformFromModel(): void {}

  /**
   * @virtual
   * Called before "serializing" into a DTO
   */
  protected transformToDTO(): void {}

  /**
   * @virtual
   * Called before deserializing an exisiting DTO
   */
  protected transformFromDTO(): void {}

  /**
   * Converts nested Models to DTOs which were set using setObjectModel or setArrayModel
  */
  private transformInnerPropertiesToDTO() {
    Object.keys(this.innerArrayModels).forEach((dtoPath) => {
      const dtoArray = [] as any[];
      this.innerArrayModels[dtoPath].forEach((model) => {
        if (typeof (model ?? {}).toDTO !== 'function') {
          throw new TypeError(`Unable to retrieve DTO from ${dtoPath} for array entry, it doesn't seem to be a ViewModel`);
        }
        dtoArray.push(model?.toDTO());
      });
      (this.dto as any)[dtoPath] = dtoArray;
    });
    Object.keys(this.innerObjModels).forEach((dtoPath) => {
      const objModel = this.innerObjModels[dtoPath];
      if (Array.isArray(objModel)) {
        Vue.$log.error(`Unable to cast ${dtoPath} to a singular Model, because it was an Array`);
        Vue.$log.error('You should be using getArrayModels and setArrayModels for Arrays');
        return;
      }
      (this.dto as any)[dtoPath] = objModel?.toDTO();
    });
  }

  toDTO() {
    this.transformInnerPropertiesToDTO();
    this.transformToDTO();
    // Return immutable copy (no references)
    return _.deepClone(this.dto) as Tdto;
  }

  /** @deprecated use toDTO instead */
  getDTO() {
    return this.toDTO();
  }

  // ______________________________________________________________________________________
  // Validation
  // ______________________________________________________________________________________

  public schema?: IJSONSchema = undefined;
  public errors: IValidationError[] = [];

  public get valid() {
    if (!this.schema) {
      return true;
    }
    const errors = this.getValidationErrors(this.schema);
    return errors.length === 0;
  }

  private getValidationErrors(schema: IJSONSchema, propName?: string) {
    return Vue.$validation.validate(schema, this, propName);
  }

  public validateWithSchema(schema: IJSONSchema, propName?: string) {
    let errors = this.getValidationErrors(schema, propName);
    const isValid = errors.length === 0;
    if (propName !== undefined) {
      this.clearPropErrors(propName);
      errors = [...this.errors, ...errors];
    }
    this.errors = errors;
    return isValid;
  }

  public validate(propName?: string) {
    if (this.schema) {
      return this.validateWithSchema(this.schema, propName);
    }
    Vue.$log.warning('Validate was called on a model without defining a schema');
    return true;
  }

  protected clearPropErrors(propName: string) {
    this.errors = this.errors.filter((error) => error.propName !== propName && error.path?.join('.') !== propName);
  }
}
